import React from 'react';
import classes from './ScheduleListItem.module.scss';
import DynamicFilledButton from "../../../UI/DynamicFilledButton/DynamicFilledButton";
import Label from "../../../UI/Label/Label";
import {reduceGroupsBySpecialityCode} from "../../../../services/scheduleService";
import {Tooltip} from "antd";
import Avatar from "antd/es/avatar/avatar";
import {AiOutlineUser} from "react-icons/ai";
import {IoMegaphoneOutline} from "react-icons/io5";
import {formatFioWithInitials} from "../../../../services/generalService";

const ScheduleListItem = ({lesson, scheduleType, style = {}, displayDate = true, isCurrentLesson, className}) => {

    const getFormattedWeekNumbers = (weekNumbers) => {
        return weekNumbers ? weekNumbers.join(', ').concat(' нед.') : '';
    }

    const getFormattedLessonTime = (dateStr) => {
        const dateWithoutYear = dateStr.substring(0, 5);
        if (dateStr[0] === "0") {
            return dateWithoutYear.substring(1);
        }
        return dateWithoutYear;
    }

    const getLessonTypeLabel = (lessonType) => {
        let colors;
        if (['ЛР', 'УЛР'].includes(lessonType)) {
            colors = {primary: '#F98F43', secondary: '#FFE7D7'};
        } else if (['ЛК', 'УЛк'].includes(lessonType)) {
            colors = {primary: '#469E3E', secondary: '#E8FFE5'};
        } else if (['ПЗ', 'УПз'].includes(lessonType)) {
            colors = {primary: '#8E4FEC', secondary: '#F0E5FF'};
        } else if (lessonType === 'Консультация') {
            colors = {primary: '#48B0EB', secondary: '#E8F5FC'};
        } else if (['Экзамен', 'Канд. экзамен'].includes(lessonType)) {
            colors = {primary: '#F95B5E', secondary: '#F8E9E9'};
        } else if (['Канд. зачет', 'Дифф. зачет', 'Канд. дифф. зачет', 'Зачет'].includes(lessonType)) {
            colors = {primary: '#DABA4B', secondary: '#FFFAE0'};
        }

        return <Label styles={{fontSize: '14px'}}
                      text={lessonType}
                      colors={colors}/>;
    }

    const getGroupsLabel = (group, index) => {
        const getGroupLink = (reducedGroupName) => {
            const indexOfHyphen = reducedGroupName.indexOf('-');
            const groupName = (indexOfHyphen === -1) ?
                reducedGroupName : reducedGroupName.substr(0, indexOfHyphen);
            return `/schedule/${groupName}`;
        }

        const content = (<DynamicFilledButton
            text={group.name}
            href={getGroupLink(group.name)}
            onClick={() => localStorage.setItem('scheduleUrlId', JSON.stringify(group.name.substr(0, group.name.indexOf('-'))))}
            styles={{fontSize: '15px'}}/>);

        return (<React.Fragment key={`groupWrapper${index}`}>
            <span className={classes.desktopClickableLabel}>
                <Tooltip placement="top"
                         title={group.specialityCode + ' ' + group.specialityName + ' (' + group.numberOfStudents + ' студентов)'}>
                    <span style={{display: 'inline-block'}}>{content}</span>
                </Tooltip>
            </span>
            <span className={classes.mobileClickableLabel}>
                {content}
            </span>
        </React.Fragment>);
    }

    const getEmployeeLabel = (employee, index) => {
        const getFormattedDegreeAndRank = (employee) => {
            let degreeAndRankString = employee.degreeAbbrev ?? '';
            if (employee.degreeAbbrev && employee.rank) {
                degreeAndRankString += `, ${employee.rank}`;
            }
            return degreeAndRankString ? '('.concat(degreeAndRankString, ')') : '';
        }

        const content = <DynamicFilledButton
            text={formatFioWithInitials(employee)}
            href={`/schedule/${employee.urlId}`}
            onClick={() => localStorage.setItem('scheduleUrlId', JSON.stringify(employee.urlId))}
            styles={{width: 'fit-content', padding: '0 10px', textAlign: 'left', fontSize: '15px'}}/>

        return <React.Fragment key={`employeeLabelWrapper${index}`}>
            <span className={classes.desktopClickableLabel}>
                <Tooltip
                    placement="top"
                    title={<React.Fragment>
                        <div className={classes.avatarContainer}>
                            <Avatar
                                size={90}
                                src={employee.photoLink}
                                icon={<AiOutlineUser/>}/>
                        </div>
                        <div style={{textAlign: 'center'}}>
                            {`${employee.lastName} ${employee.firstName} ${employee.middleName}`}
                        </div>
                        <div style={{textAlign: 'center'}}>{getFormattedDegreeAndRank(employee)}</div>
                    </React.Fragment>}>
                    <span>{content}</span>
                </Tooltip>
            </span>
            <span className={classes.mobileClickableLabel}>
                {content}
            </span>
        </React.Fragment>;
    }

    const isExam = () => {
        return !!lesson.dateLesson;
    }

    const getSubjectElement = () => {
        const subjectName = <span className={classes.mainText} style={{marginRight: '6px', cursor: 'default'}}>
            {lesson.subject}
        </span>;

        return lesson.subjectFullName ?
            <Tooltip
                overlayStyle={{maxWidth: '200px'}}
                placement="topLeft"
                title={lesson.subjectFullName}>
                {subjectName}
            </Tooltip> : subjectName;
    }

    const getMainInfo = () => {
        if (!lesson.announcement) {
            return (<div>
                {getSubjectElement()}
                {getLessonTypeLabel(lesson.lessonTypeAbbrev)}

                {lesson.numSubgroup !== 0 &&
                <span className={classes.subgroupLabel}>
                    {`${lesson.numSubgroup} п.`}
                </span>}
            </div>);
        }
        return (<div className={classes.announcementHeader}>
            <IoMegaphoneOutline size={20}/>
            <span>Объявление</span>
        </div>);
    }

    const getSecondRowInfo = () => {
        if (!lesson.announcement) {
            return (<div className={classes.studyPeriod}>
                    {lesson.weekNumber.length !== 4 && `${getFormattedWeekNumbers(lesson.weekNumber)} `}
                <span style={{ color: '#939393' }}>
                    {`(с ${lesson.startLessonDate.substring(0, 5)} по ${lesson.endLessonDate.substring(0, 5)})`}
                </span>
            </div>);
        }
        return (<div className={classes.announcementNote}>
            <div style={{lineHeight: '1.15'}}>{lesson.note}</div>
            <div>Дата: {lesson.startLessonDate}</div>
        </div>);
    }

    return (
        <div className={`${classes.container} ${className} ${isCurrentLesson && classes.currentLesson}`} style={style}>
            <div className={classes.lessonTime}>
                <div className={classes.startTime}>
                    {getFormattedLessonTime(lesson.startLessonTime)}
                </div>
                <div className={classes.endTime}>
                    {getFormattedLessonTime(lesson.endLessonTime)}
                </div>
            </div>
            <div className={classes.verticalDivider}/>
            <div style={{flexGrow: '1', marginLeft: '7px'}}>
                <div className={classes.firstLine}>
                    {getMainInfo()}

                    <div className={classes.additionalInfo}>
                        {/* TODO: display all auditories */}
                        {lesson.auditories &&
                        <span className={classes.auditoryInfo}>{lesson.auditories[0]}</span>}

                        <span className={classes.clickableLabels}>
                            {scheduleType === 2 &&
                            reduceGroupsBySpecialityCode(lesson.studentGroups)?.map((group, index) => getGroupsLabel(group, index))}
                            {scheduleType === 1 &&
                            lesson.employees?.map((employee, index) => getEmployeeLabel(employee, index))}
                        </span>
                    </div>
                </div>
                {(isExam() && displayDate) &&
                <div className={classes.dateAndTimeOfExam}>
                    <div style={{marginTop: '4px'}}>
                        <span>Дата: </span>
                        <b>{lesson.dateLesson}</b>
                    </div>
                </div>}

                {!isExam() && getSecondRowInfo()}

                {lesson.note && <div className={classes.note}>
                    {lesson.note}
                </div>}
            </div>
        </div>);
};

export default ScheduleListItem;
